import { SetStateAction, useCallback, useState } from 'react';

import { useTopAssets, useTopAssetsSettings } from '@kaltura-ott/tvpil-shared';
import { TopAsset } from '@kaltura-ott/tvpil-shared/lib/rhino/data/topAssets/types';

import { RailProviderData, RailSettings } from 'components/widgets/Rail/components/RailProvider/types';
import { useHandleSkeletonRef } from 'components/widgets/Rail/hooks/useHandleSkeletonRef/useHandleSkeletonRef';
import { DEFAULT_IMAGE_PARAMS } from 'consts';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';

function useTopRail(referenceId: string) {
  const originalSettings = useTopAssetsSettings({
    referenceId,
    ...DEFAULT_IMAGE_PARAMS
  });
  const originalData = useTopAssets(referenceId);

  const [shouldPreventDisplay, setShouldPreventDisplay] = useState(true);

  const setShouldPreventDisplayForward = useCallback(
    (value: SetStateAction<boolean>) => setShouldPreventDisplay(value),
    [],
  );

  const hasNoData = !originalData.items.length && !originalData.isLoading;
  const isRailEmpty = hasNoData || Boolean(originalData.error);

  const data: RailProviderData<TopAsset> = {
    items: originalData.items,
    isLoading: originalData.isLoading,
    error: originalData.error,
    refetch: originalData.refetch,
  };
  const settings: RailSettings = {
    branding: originalSettings?.branding!,
    id: originalSettings?.id,
    imageHeight: originalSettings?.imageHeight,
    imageType: originalSettings?.imageType,
    imageWidth: originalSettings?.imageWidth,
    title: originalSettings?.title!,
    shouldShowRating: originalSettings?.shouldShowRating || false,
    shouldShowEntitlementIndicator: originalSettings?.shouldShowEntitlementIndicator || false,
  };
  const handleSkeletonRef = useHandleSkeletonRef(setShouldPreventDisplayForward, originalData.items, settings);

  useRhinoErrorReaction({ error: originalData.error, refetch: originalData.refetch });

  return {
    data,
    settings: originalSettings ? settings : null,
    isRailEmpty,
    shouldPreventDisplay,
    handleSkeletonRef,
  };
}

export { useTopRail };
